import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  RefreshCw,
  ExternalLink,
  ArrowRightLeft,
  Filter,
  Package,
  History,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  getFreeStockList,
  formatFreeStockDetails,
  type FreeStockItem,
} from '@/services/warehouse/freeStockApi'
import { FreeStockDetailModal } from './FreeStockDetailModal'

interface FreeStockTabProps {
  onConvertToMaterial?: (item: FreeStockItem) => void
  onOpenConversionHistory?: () => void
  initialItems?: FreeStockItem[]
}

interface ContextMenuState {
  x: number
  y: number
  item: FreeStockItem
}

export function FreeStockTab({
  onConvertToMaterial,
  onOpenConversionHistory,
  initialItems,
}: FreeStockTabProps): React.ReactElement {
  const [items, setItems] = useState<FreeStockItem[]>(initialItems ?? [])
  const [loading, setLoading] = useState(initialItems === undefined)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [poFilter, setPoFilter] = useState('ALL')

  const [selectedDetailItemId, setSelectedDetailItemId] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getFreeStockList()
      setItems(data)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải danh sách hàng tự do')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialItems === undefined) {
      void loadData()
    }
  }, [loadData, initialItems])

  // Close context menu on outside click or Escape key
  useEffect(() => {
    function handleClickOutside() {
      setContextMenu(null)
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setContextMenu(null)
      }
    }
    if (contextMenu) {
      window.addEventListener('click', handleClickOutside)
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('click', handleClickOutside)
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [contextMenu])

  // Extract distinct POs for filter dropdown
  const distinctPos = useMemo(() => {
    const map = new Map<string, string>()
    items.forEach((item) => {
      const code = item.po_code || item.po_id
      if (code && !map.has(item.po_id)) {
        map.set(item.po_id, code)
      }
    })
    return Array.from(map.entries()).map(([id, code]) => ({ id, code }))
  }, [items])

  // Filter items
  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (poFilter !== 'ALL' && item.po_id !== poFilter && item.po_code !== poFilter) {
        return false
      }
      if (search.trim()) {
        const q = search.toLowerCase()
        const matchName = item.name.toLowerCase().includes(q)
        const matchPo = (item.po_code || '').toLowerCase().includes(q)
        const matchDetails = formatFreeStockDetails(item.details).toLowerCase().includes(q)
        if (!matchName && !matchPo && !matchDetails) return false
      }
      return true
    })
  }, [items, search, poFilter])

  // Total free stock items count
  const totalStockQty = useMemo(() => {
    return items.reduce((sum, i) => sum + (i.qty_on_hand || 0), 0)
  }, [items])

  function handleRowContextMenu(e: React.MouseEvent, item: FreeStockItem) {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item,
    })
  }

  return (
    <div className="space-y-3 font-sans">
      {/* Top action / filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search box */}
          <div className="relative min-w-[220px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên hàng, chi tiết, mã PO..."
              className="h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white pl-8 pr-3 text-xs outline-none focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
            />
          </div>

          {/* Filter by PO */}
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={poFilter}
              onChange={(e) => setPoFilter(e.target.value)}
              className="h-9 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 text-xs outline-none focus:border-[var(--color-accent)]"
            >
              <option value="ALL">Tất cả PO</option>
              {distinctPos.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.code}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadData()}
            disabled={loading}
            title="Tải lại danh sách"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Right buttons */}
        <div className="flex items-center gap-2">
          {onOpenConversionHistory ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onOpenConversionHistory}
              title="Xem lịch sử chuyển đổi hàng tự do sang vật tư"
              className="whitespace-nowrap"
            >
              <History className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              Lịch sử chuyển sang VT
            </Button>
          ) : null}
          <div className="text-xs text-slate-500 whitespace-nowrap">
            Tổng dòng tồn: <b className="text-slate-800">{items.length}</b> ({filtered.length} hiển thị) · Tổng số lượng: <b className="text-emerald-700 tabular-nums">{totalStockQty.toLocaleString('vi-VN')}</b>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-[var(--radius-md)] border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error}
        </div>
      ) : null}

      {/* Main Table */}
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white shadow-xs">
        <table className="w-full border-collapse text-left text-xs">
          <thead className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
            <tr>
              <th className="w-12 px-3 py-2.5 text-center whitespace-nowrap">STT</th>
              <th className="min-w-[180px] px-3 py-2.5">Tên hàng</th>
              <th className="min-w-[200px] px-3 py-2.5">Chi tiết</th>
              <th className="w-20 px-3 py-2.5 whitespace-nowrap">ĐVT</th>
              <th className="w-28 px-3 py-2.5 text-right whitespace-nowrap">Tồn</th>
              <th className="min-w-[150px] px-3 py-2.5 whitespace-nowrap">Từ PO</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-slate-400">
                  <div className="inline-flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                    Đang tải danh sách kho hàng tự do...
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-slate-400">
                  <Package className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                  {items.length === 0
                    ? 'Chưa có hàng tự do nào trong kho. Hàng tự do sẽ được ghi nhận khi nhận hàng từ PO FREE.'
                    : 'Không tìm thấy dòng hàng tự do phù hợp điều kiện lọc.'}
                </td>
              </tr>
            ) : (
              filtered.map((item, index) => {
                const detailsStr = formatFreeStockDetails(item.details)
                return (
                  <tr
                    key={item.id}
                    tabIndex={0}
                    onClick={() => setSelectedDetailItemId(item.id)}
                    onContextMenu={(e) => handleRowContextMenu(e, item)}
                    onKeyDown={(e) => {
                      if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                        e.preventDefault()
                        e.stopPropagation()
                        const rect = e.currentTarget.getBoundingClientRect()
                        setContextMenu({
                          x: Math.min(rect.left + 80, window.innerWidth - 240),
                          y: Math.min(rect.bottom - 10, window.innerHeight - 140),
                          item,
                        })
                      } else if (e.key === 'Enter') {
                        setSelectedDetailItemId(item.id)
                      }
                    }}
                    className="cursor-pointer transition-colors hover:bg-slate-50/90 focus:outline-none focus:bg-slate-50/90"
                    title="Bấm để xem lịch sử biến động · Chuột phải hoặc Shift+F10 để chuyển sang vật tư"
                  >
                    {/* Cột 1: STT */}
                    <td className="px-3 py-2.5 text-center text-slate-400 tabular-nums whitespace-nowrap">
                      {index + 1}
                    </td>

                    {/* Cột 2: Tên hàng */}
                    <td className="px-3 py-2.5 font-semibold text-slate-900 break-words">
                      {item.name}
                    </td>

                    {/* Cột 3: Chi tiết (gom các cột tự thêm của PO) */}
                    <td className="px-3 py-2.5 text-slate-600 break-words">
                      {detailsStr}
                    </td>

                    {/* Cột 4: ĐVT */}
                    <td className="px-3 py-2.5 text-slate-700 font-medium whitespace-nowrap">
                      {item.uom}
                    </td>

                    {/* Cột 5: Tồn */}
                    <td className="px-3 py-2.5 text-right font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                      {item.qty_on_hand.toLocaleString('vi-VN')}
                    </td>

                    {/* Cột 6: Từ PO */}
                    <td
                      className="px-3 py-2.5 whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link
                        to={`/purchasing/po/${item.po_id}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-[var(--color-link)] hover:underline whitespace-nowrap"
                        title="Mở đơn mua hàng PO"
                      >
                        <span>{item.po_code || item.po_id}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </Link>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Right-click Context Menu */}
      {contextMenu ? (
        <div
          className="fixed z-50 min-w-48 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white py-1 text-xs shadow-lg font-sans"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-slate-100 px-3 py-1 font-semibold text-slate-500 whitespace-nowrap">
            {contextMenu.item.name}
          </div>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-100 whitespace-nowrap"
            onClick={() => {
              const it = contextMenu.item
              setContextMenu(null)
              onConvertToMaterial?.(it)
            }}
          >
            <ArrowRightLeft className="h-3.5 w-3.5 text-blue-600 shrink-0" />
            <span>Chuyển sang vật tư…</span>
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-100 whitespace-nowrap"
            onClick={() => {
              const id = contextMenu.item.id
              setContextMenu(null)
              setSelectedDetailItemId(id)
            }}
          >
            <Package className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <span>Xem lịch sử biến động</span>
          </button>
        </div>
      ) : null}

      {/* Detail Modal */}
      <FreeStockDetailModal
        itemId={selectedDetailItemId}
        onClose={() => setSelectedDetailItemId(null)}
      />
    </div>
  )
}
